import {
  Component,
  OnInit,
  ViewEncapsulation,
  Input,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ChartDataSets, ChartOptions } from "chart.js";
import { Color, Label } from "ng2-charts";

import { ZoneComponent } from "@/shared-module/components";
import { LineChartDatasource } from "@/shared-module/models";
import { DialogService } from "@/shared-module/services";

@Component({
  selector: "app-linechart",
  templateUrl: "linechart.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class LineChartComponent implements ZoneComponent, OnInit {
  @ViewChild("template", { static: true }) template;
  @Input() data: LineChartDatasource;
  @Input() width: number;
  @Input() needpaddingLeft: boolean;
  @Input() isZoom: boolean;

  public lineChartData: ChartDataSets[] = [];
  public lineChartLabels: Label[] = [];
  public lineChartColors: Color[] = [];
  public lineChartLegend = true;
  public lineChartType = "line";

  public lineChartOptions: ChartOptions = {
    aspectRatio: 1.5,
    tooltips: {
      callbacks: {
        title: (items, data) => {
          return this.data.xLabels[items[0].index];
        },
        label: (tooltipItem, data) => {
          let label = data.datasets[tooltipItem.datasetIndex].label || "";

          if (label) {
            label += ": ";
          }
          label +=
            this.data.series[tooltipItem.datasetIndex].yValues[
              tooltipItem.index
            ].yLabel;
          return label;
        },
      },
    },
    responsive: true,
    scales: {
      gridLines: {
        drawBorder: false,
        zeroLineColor: "transparent",
        display: false,
      },
      xAxes: [
        {
          id: "x-axis-0",
          offset: true,
          gridLines: {
            drawBorder: false,
            zeroLineColor: "transparent",
            display: false,
          },
        },
      ],
      yAxes: [
        {
          id: "y-axis-0",
          gridLines: {
            drawBorder: false,
            zeroLineColor: "transparent",
            display: false,
          },
          ticks: {
            callback: (value: number) => {
              if (this.data.axisY.isPercentage)
                return Math.round(value * 1000) / 10 + "%";
              if (this.data.axisY.isPercentageUnit) 
                return value + "%";
              else if (this.data.axisY.unit)
                return value + " " + this.data.axisY.unit;
              else
                return value; 
            },
          },
        },
      ],
    },
  };

  constructor(
    private viewContainerRef: ViewContainerRef,
    private dialogService: DialogService
  ) {}

  zoom() {
    this.dialogService.setZoomInfo(this.data.zoomInfo);
  }

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.template);

    if (this.data) {
      this.lineChartData = [];
      this.lineChartColors = [];
      this.lineChartLabels = [];
      let data = [];
      let ds = {};
      let color = {};

      this.data.xValues.forEach((v) => {
        this.lineChartLabels.push(`P${v}`);
      });

      this.data.series.forEach((s) => {
        data = [];
        s.yValues.forEach((v) => {
          data.push(v.y);
        });

        let borderWidth = 1;
        if (s.isCurrentTeam) {
          borderWidth = 3;
        }

        // tslint:disable-next-line:object-literal-shorthand
        ds = { data: data, label: s.caption, fill: false };
        color = {
          // grey
          backgroundColor: s.color,
          borderColor: s.color,
          pointBackgroundColor: s.color,
          pointBorderColor: s.color,
          pointHoverBackgroundColor: s.color,
          pointHoverBorderColor: s.color,
          borderWidth: borderWidth.toString(),
        };

        this.lineChartData.push(ds);
        this.lineChartColors.push(color);
      });

      if (this.data.axisY.min != null)
      {
        this.lineChartOptions.scales.yAxes[0].ticks.min = this.data.axisY.min;
        if (this.data.axisY.min == 0)
          this.lineChartOptions.scales.yAxes[0].ticks.beginAtZero = true;
      }
      if (this.data.axisY.max != null)
      {
        this.lineChartOptions.scales.yAxes[0].ticks.max = this.data.axisY.max;
      }

      // Handle edge case: single data point with percentage values
      // Chart.js creates weird scales (e.g., -100 to +150) when there's only one point
      if ((this.data.axisY.isPercentage || this.data.axisY.isPercentageUnit) &&
          this.data.xValues.length === 1) {
        // Check if all Y values are the same
        const allYValues: number[] = [];
        this.data.series.forEach((s: any) => {
          s.yValues.forEach((v: any) => {
            allYValues.push(v.y);
          });
        });
        const allSame = allYValues.length > 0 && allYValues.every((val: number) => val === allYValues[0]);

        if (allSame && this.lineChartOptions.scales?.yAxes?.[0]?.ticks) {
          // Check if values are decimals between 0 and 1
          const allBetweenZeroAndOne = allYValues.every((val: number) => val >= 0 && val <= 1);
          const maxValue = allBetweenZeroAndOne ? 1 : 100;

          // For single identical percentage point, use 0-1 or 0-100 scale
          if (this.lineChartOptions.scales.yAxes[0].ticks.min == null) {
            this.lineChartOptions.scales.yAxes[0].ticks.min = 0;
            this.lineChartOptions.scales.yAxes[0].ticks.beginAtZero = true;
          }
          if (this.lineChartOptions.scales.yAxes[0].ticks.max == null) {
            this.lineChartOptions.scales.yAxes[0].ticks.max = maxValue;
          }
        }
      }
    }
  }
}
